import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import { withAlpha } from 'utils/colorUtils';
import type { NotificationItem } from './index';

// assets
import { IconBell } from '@tabler/icons-react';

// ── Topic → Route mapping ─────────────────────────────────────────────────────
function getRouteByTopic(topic: string): string {
  const t = topic.toLowerCase();
  if (t === 'ndr') return '/ndr';
  if (t === 'tracking' || t === 'shipment') return '/customer/shipment';
  if (t === 'payout') return '/billing';
  if (t.includes('weight')) return '/weightdiscrepency';
  return '';
}

// ── Helper: relative time ─────────────────────────────────────────────────────
function relativeTime(dateStr: string): string {
  const created = new Date(dateStr);
  if (isNaN(created.getTime())) return '';
  const diffMs = Date.now() - created.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs} hr ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

// ── Topic → avatar colour ─────────────────────────────────────────────────────
const TOPIC_COLORS: Record<string, { color: string; bgcolor: string }> = {
  'Weight Discrepancy': { color: 'warning.dark', bgcolor: 'warning.light' },
  Tracking: { color: 'error.dark', bgcolor: 'error.light' },
  Payout: { color: 'success.dark', bgcolor: 'success.light' },
  Order: { color: 'primary.dark', bgcolor: 'primary.light' }
};

function topicColors(topic: string) {
  return TOPIC_COLORS[topic] ?? { color: 'secondary.dark', bgcolor: 'secondary.light' };
}

// ── Item wrapper ──────────────────────────────────────────────────────────────
function ListItemWrapper({
  children,
  unread,
  onClick
}: {
  children: React.ReactNode;
  unread: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        bgcolor: unread ? withAlpha(theme.palette.warning.light, 0.12) : 'transparent',
        '&:hover': {
          bgcolor: withAlpha(theme.palette.grey[200], 0.4),
          ...theme.applyStyles('dark', { bgcolor: 'dark.900' })
        }
      }}
    >
      {children}
    </Box>
  );
}

// ==============================|| NOTIFICATION LIST ||============================== //

interface NotificationListProps {
  notifications: NotificationItem[];
  onMarkAsRead: (id: number) => void;
  onClose?: () => void;
}

export default function NotificationList({ notifications, onMarkAsRead, onClose }: NotificationListProps) {
  const navigate = useNavigate();

  // Find the ID of the most recently created notification
  const latestId =
    notifications.length > 0
      ? notifications.reduce((prev, curr) =>
        new Date(curr.CREATED_AT) > new Date(prev.CREATED_AT) ? curr : prev
      ).ID
      : null;

  if (notifications.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4, gap: 1 }}>
        <IconBell size={36} stroke={1} style={{ opacity: 0.3 }} />
        <Typography variant="body2" color="text.secondary">
          No notifications found
        </Typography>
      </Stack>
    );
  }

  const handleNotificationClick = (item: NotificationItem) => {
    // Only call markAsRead API if the notification is unread
    if (item.IS_READ === 0) {
      onMarkAsRead(item.ID);
    }
    const route = getRouteByTopic(item.NOTIFICATION_TOPIC);
    if (route) {
      onClose?.();
      navigate(route);
    }
  };

  return (
    <List sx={{ width: '100%', maxWidth: { xs: 300, md: 330 }, py: 0 }}>
      {notifications.map((item) => {
        const { color, bgcolor } = topicColors(item.NOTIFICATION_TOPIC);
        const isUnread = item.IS_READ === 0;

        return (
          <ListItemWrapper
            key={item.ID}
            unread={isUnread}
            onClick={() => handleNotificationClick(item)}
          >
            <ListItem
              alignItems="center"
              disablePadding
              secondaryAction={
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: '-15px' }}>
                  {relativeTime(item.CREATED_AT)}
                </Typography>
              }
            >
              <ListItemAvatar>
                <Avatar sx={{ color, bgcolor }}>
                  <IconBell stroke={1.5} size="18px" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="subtitle2" fontWeight={isUnread ? 700 : 400} noWrap>
                    {item.TITLE.length > 22 ? `${item.TITLE.slice(0, 22)}...` : item.TITLE}
                  </Typography>
                }
              />
            </ListItem>

            {/* Message + chips */}
            <Stack sx={{ gap: 0.75, pl: 7 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {item.MESSAGE}
              </Typography>
              <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
                <Chip
                  label={item.NOTIFICATION_TOPIC}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
                {isUnread && (
                  <Chip label="Unread" color="warning" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                {item.ID === latestId && (
                  <Chip
                    label="New"
                    color="success"
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                  />
                )}
              </Stack>
            </Stack>
          </ListItemWrapper>
        );
      })}
    </List>
  );
}
